import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useStratagems } from "@/context/StratagemContext";
import { useAuth } from "@/context/AuthContext";
import ArrowDisplay from "@/components/ArrowDisplay";
import ReportIssueModal from "@/components/ReportIssueModal";
import { fadeInUp, transition } from "@/utils/motion";
import { play as sfx, playHover } from "@/utils/sounds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slugify = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function getCategoryClass(item, activeCategory, newCount) {
  if (activeCategory === item) return "border-[#FFE800] text-[#FFE800]";
  if (item === "new" && newCount > 0)
    return "border-green-500/60 text-green-400 hover:text-green-300 hover:border-green-400";
  return "border-white/10 text-gray-400 hover:text-[#FFE800] hover:border-[#FFE800]/40";
}

function getCategoryLabel(item, newCount) {
  if (item === "all") return "All Categories";
  if (item === "new") return `New (${newCount})`;
  return item;
}

const Library = () => {
  const { stratagems, loading, error } = useStratagems();
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [reportTarget, setReportTarget] = useState(null);
  const navigate = useNavigate();

  const handleReportSubmit = useCallback(async (note) => {
    if (!reportTarget) return;
    try {
      await axios.post(`${API}/stratagems/${reportTarget.id}/report`, { reason: note });
      toast.success("Report submitted — thank you, Helldiver.");
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("You can only submit 1 report per day.");
      } else {
        toast.error(err.response?.data?.detail || "Unable to submit report.");
      }
    } finally {
      setReportTarget(null);
    }
  }, [reportTarget]);

  const categories = useMemo(() => {
    const unique = new Set();
    stratagems.forEach((item) => {
      if (item.category) unique.add(item.category);
      if (item.categories) item.categories.forEach((c) => unique.add(c));
    });
    return ["all", "new", ...Array.from(unique).sort()];
  }, [stratagems]);

  const newCount = useMemo(() => {
    return stratagems.filter((item) => item.is_new).length;
  }, [stratagems]);

  const filtered = useMemo(() => {
    return stratagems.filter((item) => {
      const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
      if (category === "new") return matchesSearch && item.is_new;
      if (category === "all") return matchesSearch;
      const matchesCategory =
        item.category === category ||
        (item.categories && item.categories.includes(category));
      return matchesSearch && matchesCategory;
    });
  }, [stratagems, search, category]);

  const goToDrill = (id) => {
    sfx("click");
    navigate(`/practice?target=${id}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 md:px-12" data-testid="library-page">
      <motion.section
        {...fadeInUp}
        transition={transition}
        className="hud-card clip-corners p-6"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1
              className="font-headings text-4xl uppercase tracking-widest text-[#FFE800]"
              data-testid="library-title"
            >
              Stratagem Catalog
            </h1>
            <p className="text-sm text-gray-400" data-testid="library-subtitle">
              Click any stratagem to start drilling.
            </p>
          </div>
        </div>

        <div className="mt-6 relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search stratagem name"
            className="w-full bg-black/50 border border-white/10 text-[#FFE800] font-data text-sm py-2 pl-10 pr-3 focus:border-[#FFE800] outline-none"
            data-testid="library-search-input"
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-2" data-testid="library-category-filter">
          {categories.map((item) => (
            <button
              key={item}
              onClick={() => { sfx("tab"); setCategory(item); }}
              onMouseEnter={playHover}
              className={`border px-3 py-2 text-xs font-headings uppercase tracking-[0.2em] ${getCategoryClass(item, category, newCount)}`}
              data-testid={`library-category-${slugify(item)}`}
            >
              {getCategoryLabel(item, newCount)}
            </button>
          ))}
        </div>

        {loading && (
          <p className="mt-4 text-sm text-gray-400" data-testid="library-loading">
            Loading stratagem catalog...
          </p>
        )}
        {error && (
          <p className="mt-4 text-sm text-red-400" data-testid="library-error">
            {error}
          </p>
        )}
      </motion.section>

      <div className="mt-8 grid gap-6 lg:grid-cols-2" data-testid="library-grid">
        {filtered.map((item) => (
          <div
            key={item.id}
            className="hud-card clip-corners p-6 group cursor-pointer hover:border-[#FFE800]/40 transition-colors duration-150"
            onClick={() => goToDrill(item.id)}
            onMouseEnter={playHover}
            data-testid={`library-card-${item.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p
                    className="text-xs uppercase tracking-[0.3em] text-gray-500"
                    data-testid={`library-card-category-${item.id}`}
                  >
                    {item.category}
                  </p>
                  {item.is_new && (
                    <span
                      className="px-2 py-0.5 text-[10px] font-bold bg-green-500 text-black rounded"
                      data-testid={`library-new-badge-${item.id}`}
                    >
                      NEW
                    </span>
                  )}
                </div>
                <h3
                  className="mt-2 font-headings text-2xl uppercase tracking-wide text-white group-hover:text-[#FFE800] transition-colors duration-150"
                  data-testid={`library-card-name-${item.id}`}
                >
                  {item.name}
                </h3>
              </div>
              {user && (
                <button
                  onClick={(e) => { e.stopPropagation(); sfx("click"); setReportTarget(item); }}
                  onMouseEnter={(e) => { e.stopPropagation(); playHover(); }}
                  className="text-gray-600 hover:text-red-400 transition-colors duration-150 mt-1"
                  title="Report incorrect code"
                  data-testid={`library-report-button-${item.id}`}
                >
                  <AlertTriangle className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="mt-4">
              <ArrowDisplay sequence={item.code} size="sm" testIdPrefix={`library-${item.id}`} />
            </div>
          </div>
        ))}
      </div>

      <ReportIssueModal
        open={Boolean(reportTarget)}
        stratagem={reportTarget}
        onClose={() => setReportTarget(null)}
        onSubmit={handleReportSubmit}
      />
    </div>
  );
};

export default Library;
